import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FilterSearch as FilterIcon } from '@refera/ui-icons'
import { Button, Datagrid, Loader } from '@refera/ui-web'
import Theme from '@refera/ui-core'

import { navigate } from '@reach/router'
import { Grid } from '@mui/material'

import { useToast } from '_/hooks/use-toast'
import useRolePermission from '_/hooks/use-role-permission'

import { ROUTES } from '_/utils/constants'
import { handleDatagridOrderBy } from '_/utils/helpers'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

import { getWidgetsList } from '_/modules/widgets/actions'
import { getWidgetsListSelector } from '_/modules/widgets/selectors'

import { TABLE_SX } from '../profiles/constants'
import { formatErrorMessage } from '../finance/utils/FormatErrorMessage'

import HeaderTitle from '_/components/header-title'

import { WidgetsListColumns } from './columns'
import { WidgetFilter } from './widget-filter'

import useStyles from './styles'
import { formatFilter } from './utils'

const DEFAULT_ORDER = 'title'

export const ManageWidgets = () => {
  const styles = useStyles()

  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { checkIfUserDoesNotPermission } = useRolePermission()

  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [orderBy, setOrderBy] = useState('')
  const [filters, setFilters] = useState({})
  const [pageSize, setPageSize] = useState(100)
  const [hasPermissionToViewPage, setHasPermissionToViewPage] = useState(false)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const widgetsList = useSelector(getWidgetsListSelector)

  function fetchWidgetsList(filterParams = null) {
    if (!hasPermissionToViewPage) return

    setLoading(true)
    const formattedFilters = formatFilter(filterParams || filters)
    const params = { ...formattedFilters, orderBy, pageSize, page: page + 1 }
    dispatch(getWidgetsList(params))
      .catch(err => showToast({ message: formatErrorMessage(err), type: 'error' }))
      .finally(() => setLoading(false))

    if (filterParams) setFilters(filterParams)
  }

  function handleGoToWidgetDetails(widgetId = null) {
    window.open(`${ROUTES.MANAGE_WIDGETS}/${widgetId || 'novo'}`)
  }

  function handleFilters() {
    setFilterDrawerOpen(prevState => !prevState)
  }

  const handleOrderBy = useCallback(
    orderObj => {
      setOrderBy(handleDatagridOrderBy({ order: orderObj, defaultOrder: DEFAULT_ORDER }))
    },
    [setOrderBy]
  )

  const Filters = useMemo(
    () => (
      <WidgetFilter
        open={filterDrawerOpen}
        close={() => setFilterDrawerOpen(false)}
        applyFilters={fetchWidgetsList}
        filters={filters}
        setFilters={setFilters}
      />
    ),
    [filterDrawerOpen, filters, fetchWidgetsList]
  )

  useEffect(() => {
    const shouldRedirectUser = checkIfUserDoesNotPermission(
      PERMISSIONS.WIDGETS,
      PERMISSIONS_ACTIONS.READ
    )

    if (shouldRedirectUser) {
      navigate(ROUTES.ACCESS_DENIED)
    } else {
      setHasPermissionToViewPage(true)
    }
  }, [])

  useEffect(() => {
    fetchWidgetsList()
  }, [hasPermissionToViewPage, page, pageSize, orderBy])

  if (!hasPermissionToViewPage) {
    return <Loader hasBackdrop open />
  }

  return (
    <Grid>
      <HeaderTitle title="Widgets">
        <Grid className={styles.buttonsContainer}>
          <Button variant="primary" onClick={() => handleGoToWidgetDetails()}>
            Novo
          </Button>
          <Button
            variant="ghost"
            startIcon={<FilterIcon color={Theme.Colors.Primary.Base} />}
            onClick={handleFilters}
            disabled={loading}
          >
            Filtros
          </Button>
        </Grid>
      </HeaderTitle>
      {Filters}
      <Grid className={styles.container}>
        <Datagrid
          loading={loading}
          rows={widgetsList?.results?.length ? widgetsList?.results : []}
          columns={WidgetsListColumns}
          paginationMode="server"
          onPageChange={setPage}
          page={page}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowCount={widgetsList?.count}
          onSortModelChange={handleOrderBy}
          disableSelectionOnClick
          onCellClick={({ row }) => handleGoToWidgetDetails(row.id)}
          sx={TABLE_SX}
        />
      </Grid>
    </Grid>
  )
}
